import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {FormControl, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommentService} from '../../../api/services/comment.service';
import {Comment, CommentEntityType} from '../../../api/interfaces/comment';
import {DatePipe, NgIf, NgTemplateOutlet} from '@angular/common';
import {MatDivider} from '@angular/material/divider';
import {User} from '../../../api/interfaces/user.interface';
import {UserService} from '../../../api/services/user.service';
import {AutoSelectInputDirective} from '../../directives/auto-select-input.directive';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltip} from '@angular/material/tooltip';
import {TOOLTIP_BASE_OPTIONS} from '../../helpers/tooltip-defaults';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {finalize, map, Subject, Subscription, switchMap, timer} from 'rxjs';
import {MapService} from '../../../api/services/map.service';
import {sum} from 'lodash';
import {TranslatePipe} from '@ngx-translate/core';
import {HasMapPermissionDirective} from '../../../packages/has-map-permission.directive';
import {MapRoleService} from '../../../acl/map-role.service';
import {Permission} from '../../../acl/map-user-roles.enum';
import {isInIframe} from '../../helpers/maporium.validators';
import {DefaultValues} from '@maporium-workspace/shared';
import {UserInitialsBadgeComponent} from "../user-menu/user-badge.component";
import {TourAnchorMatMenuDirective} from 'ngx-ui-tour-md-menu';
import {CommentsStateManager} from './comments.state-manager';
import {UndoRedoManagerService, undoRedoStateChanged$} from '../../../services/undo-redo/undo-redo-manager.service';
import {CommentCommand} from '../../../services/undo-redo/commands/comment.command';

@Component({
  standalone: true,
  selector: 'maporium-comments',
  templateUrl: './comments.component.html',
  imports: [
    ReactiveFormsModule,
    DatePipe,
    MatDivider,
    AutoSelectInputDirective,
    MatTooltip,
    CdkTextareaAutosize,
    NgTemplateOutlet,
    TranslatePipe,
    HasMapPermissionDirective,
    UserInitialsBadgeComponent,
    NgIf,
    TourAnchorMatMenuDirective
  ],
  providers: [
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: {
        ...TOOLTIP_BASE_OPTIONS,
        position: 'below'
      }
    }
  ],
  styleUrls: ['./comments.component.scss']
})
export class CommentsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entityId: string | undefined;
  @Input() entityType: CommentEntityType | undefined;
  @Input() isEditing = true;
  @Output() updated = new EventEmitter<boolean>();
  @Output() commentCount = new EventEmitter<number>();

  @ViewChildren('txtAreaValue') commentTextArea: QueryList<ElementRef> | undefined;

  public newCommentControl = new FormControl('', Validators.required);
  public comments: Comment[] = [];
  public replyToIndex: number | null = null;
  public createNewComment = false;
  /** Shown alongside the beep when the user clicks away from an unfinished comment. */
  public pendingCommentHint = false;
  public nudging = false;
  public user: User | null;
  public maxNameLength = 20;
  public currentEditingCommentElementId: string | null = null;
  public hasGuestComments = false;

  private subs = new Subscription();
  private currentEditingComment: Comment | null = null;
  private audioCtx?: AudioContext;
  private fetchTrigger$ = new Subject<{ entityId: string; emitUpdate: boolean }>();
  newGuestNameControl = new FormControl('', Validators.required);

  constructor(private commentService: CommentService,
              private mapService: MapService,
              private commentsStateManager: CommentsStateManager,
              private mapRoleService: MapRoleService,
              private userService: UserService,
              private undoRedoManager: UndoRedoManagerService) {
    if (isInIframe()) {
      this.hasGuestComments = !!this.mapService.getCurrentSelectedMapFromStore()?.hasGuestComments;
      const storageUser = this.userService.getGuestUserFromStorage();
      if (storageUser) {
        this.user = storageUser;
      } else {
        this.user = null;
      }
    } else {
      this.user = this.userService.getCurrentUserFromStorage();
    }
  }

  get newCommentDate() {
    return new Date();
  }

  hasCurrentUserPermission(permission: Permission): boolean {
    const map = this.mapService.selectedMap();
    return this.mapRoleService.hasCurrentUserPermission(permission, map);
  }

  getIsLongName(user?: User) {
    const length = this.getFullName(user).length;
    return length > this.maxNameLength;
  }

  getFullName(user?: User, comment?: Comment) {
    const guestUser = comment?.guestName || this.userService.getGuestUserFromStorage()?.name;
    return guestUser || (user ? `${user.name} ${user.lastName}` : `${this.user?.name} ${this.user?.lastName}`);
  }

  getFormattedDate(comment?: Comment) {
    return new DatePipe('en-US').transform(comment ? comment.createdAt : this.newCommentDate, 'MM/dd/yyyy hh:mm a');
  }

  get currentUser() {
    return this.userService.getCurrentUserFromStorage();
  }

  get isSmapAuthor() {
    return this.mapService.isSmapAuthor();
  }

  ngOnInit() {
    // switchMap cancels any in-flight fetch when a new one is requested, so a
    // late-arriving response for a previous entityId can never overwrite the
    // current one (MAP-1479: stale smap comments on smap switch).
    this.subs.add(
      this.fetchTrigger$
        .pipe(
          switchMap(({entityId, emitUpdate}) =>
            this.commentService.getComments(entityId).pipe(
              // Tag the response with the entityId it was fetched for so a
              // stale subscription emission can be ignored.
              map(comments => ({comments, entityId, emitUpdate}))
            )
          )
        )
        .subscribe(({comments, entityId, emitUpdate}) => {
          if (entityId !== this.entityId) return;
          this.comments = comments;
          this.commentCount.emit(comments.length + sum(comments.map(c => c.replies.length)));
          if (emitUpdate) {
            this.updated.emit(this.comments.length > 0);
          }
        })
    );

    this.subs.add(
      timer(0, 60000).subscribe(() => {
        this.getComments();
      })
    );

    // Refresh comments when undo/redo happens
    this.subs.add(
      undoRedoStateChanged$.subscribe(() => {
        this.getComments();
      })
    );

    this.subs.add(
      this.newCommentControl.valueChanges.subscribe(() => this.syncDraftState())
    );

    this.subs.add(
      this.commentsStateManager.draftNudge$.subscribe(() => {
        if (this.hasPendingDraft()) {
          this.nudgePendingDraft();
        }
      })
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
    this.commentsStateManager.setDraftActive(this, false);
    this.audioCtx?.close();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const entityIdChange = changes['entityId'];
    if (!entityIdChange || entityIdChange.firstChange) {
      return;
    }

    if (entityIdChange.currentValue !== entityIdChange.previousValue) {
      // Clear immediately so the previous smap's comments don't linger while
      // we refetch — otherwise the UI shows stale content on smap switch.
      this.comments = [];
      this.commentCount.emit(0);
      this.getComments();
    }
  }

  startCreatingComment() {
    this.createNewComment = true;

    setTimeout(() => {
      if (this.commentTextArea) {
        this.commentTextArea.first.nativeElement.focus();
      }
    }, 1000);
  }

  startEditingComment(elementId: string, comment: Comment) {
    if (this.currentUser.id !== comment.user.id) {
      return;
    }
    this.currentEditingCommentElementId = elementId;
    this.currentEditingComment = comment;
    setTimeout(() => {
      if (this.commentTextArea) {
        this.newCommentControl.setValue(comment.content);
      }
    }, 10);
  }

  stopEditingComment() {
    this.currentEditingCommentElementId = null;
    this.currentEditingComment = null;
    this.newCommentControl.reset();
  }

  addComment() {
    this.pendingCommentHint = false;
    if (this.currentEditingCommentElementId && this.currentEditingComment) {
      this.editComment(this.currentEditingComment);
      return;
    }
    const value = this.newCommentControl.value;
    if (!this.entityId && !this.entityType) {
      return;
    }
    if (this.newCommentControl.valid) {
      const guestUser = this.userService.getGuestUserFromStorage();
      const entityId = this.entityId!;
      const entityType = this.entityType!;
      const content = value!;

      if (this.replyToIndex !== null) {
        const parentId = this.comments[this.replyToIndex].id;
        this.commentService.replyToComment(parentId, content, guestUser?.name)
          .pipe(
            finalize(() => {
              this.replyToIndex = null;
            })
          )
          .subscribe((result) => {
            // Create undo command for reply
            const commentCommand = new CommentCommand(
              this.commentService,
              this.commentsStateManager,
              'add',
              {
                commentId: result.id,
                entityId,
                entityType,
                content,
                parentId
              }
            );
            this.undoRedoManager.pushCommand(commentCommand);

            this.cancelComment();
            this.getComments();
            this.updated.emit(true);
          });
      } else {
        const isFirstComment = this.comments.length === 0;
        this.commentService.addComment(entityId, entityType, content, guestUser?.name)
          .subscribe((result) => {
            // Create undo command for new comment
            const commentCommand = new CommentCommand(
              this.commentService,
              this.commentsStateManager,
              'add',
              {
                commentId: result.id,
                entityId,
                entityType,
                content,
                wasFirstComment: isFirstComment
              }
            );
            this.undoRedoManager.pushCommand(commentCommand);

            this.getComments();
            this.cancelComment();
            if (isFirstComment) {
              this.commentsStateManager.triggerFirstCommentAdded({
                elementId: entityId,
                elementType: entityType
              });
            }
            this.updated.emit(true);
          });
      }

    }
  }

  editComment(comment: Comment) {
    if (!this.currentEditingCommentElementId || this.newCommentControl.invalid) {
      return;
    }

    const previousContent = comment.content;
    const newContent = this.newCommentControl.value as string;

    this.commentService.editComment(comment.id, newContent)
      .subscribe(() => {
        // Create undo command for edit
        if (this.entityId && this.entityType) {
          const commentCommand = new CommentCommand(
            this.commentService,
            this.commentsStateManager,
            'edit',
            {
              commentId: comment.id,
              entityId: this.entityId,
              entityType: this.entityType,
              content: newContent,
              previousContent
            }
          );
          this.undoRedoManager.pushCommand(commentCommand);
        }

        this.stopEditingComment();
        this.getComments();
      });
  }

  getComments(emitUpdate = false) {
    if (!this.entityId) {
      return;
    }
    this.fetchTrigger$.next({entityId: this.entityId, emitUpdate});
  }

  cancelComment() {
    this.pendingCommentHint = false;
    this.createNewComment = false;
    this.currentEditingCommentElementId = null;
    this.currentEditingComment = null;
    this.replyToIndex = null;
    this.newCommentControl.setValue('');
  }

  onCommentBlur() {
    if (!this.createNewComment && !this.currentEditingCommentElementId) {
      return;
    }

    const value = this.newCommentControl.value?.trim();
    if (!value) {
      const editingComment = this.currentEditingComment;
      this.cancelComment();
      if (editingComment) {
        this.deleteComment(editingComment);
      }
      return;
    }

    const textArea = this.commentTextArea?.first?.nativeElement as HTMLTextAreaElement | undefined;
    if (textArea) {
      setTimeout(() => {
        textArea.focus();
        textArea.select();
      });
    }
    this.pendingCommentHint = true;
    this.beep();
  }

  private hasPendingDraft(): boolean {
    return (this.createNewComment || this.currentEditingCommentElementId !== null)
      && !!this.newCommentControl.value?.trim();
  }

  private syncDraftState() {
    this.commentsStateManager.setDraftActive(this, this.hasPendingDraft());
  }

  private nudgePendingDraft() {
    const textArea = this.commentTextArea?.first?.nativeElement as HTMLTextAreaElement | undefined;
    textArea?.focus();
    this.pendingCommentHint = true;
    this.beep();
    this.nudging = false;
    setTimeout(() => {
      this.nudging = true;
      setTimeout(() => this.nudging = false, 400);
    });
  }

  private beep() {
    try {
      if (!this.audioCtx) {
        const AudioCtx = window.AudioContext
          || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.audioCtx = new AudioCtx();
      }
      const ctx = this.audioCtx;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.value = 440;
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      oscillator.start();
      oscillator.stop(ctx.currentTime + 0.15);
    } catch {
      // Audio unavailable — ignore.
    }
  }

  deleteComment(comment: Comment) {
    if (this.currentUser?.id !== comment?.user?.id && !this.isSmapAuthor && !this.hasCurrentUserPermission('manage')) {
      return;
    }

    // Find the parent comment if this is a reply
    let parentId: string | undefined;
    for (const c of this.comments) {
      if (c.replies && c.replies.some(r => r.id === comment.id)) {
        parentId = c.id;
        break;
      }
    }

    const isLastComment = this.comments.length === 1 && !parentId;

    this.commentService.deleteComment(comment.id)
      .subscribe(() => {
        // Create undo command for delete
        if (this.entityId && this.entityType) {
          const commentCommand = new CommentCommand(
            this.commentService,
            this.commentsStateManager,
            'remove',
            {
              commentId: comment.id,
              entityId: this.entityId,
              entityType: this.entityType,
              content: comment.content,
              parentId,
              wasLastComment: isLastComment
            }
          );
          this.undoRedoManager.pushCommand(commentCommand);
        }

        if (isLastComment) {
          this.commentsStateManager.triggerNoComments(this.entityId!);
        }
        this.getComments(true);
      });
  }

  replyToComment(index: number) {
    this.replyToIndex = index;
    this.startCreatingComment();
  }

  createGuest() {
    if (!this.newGuestNameControl.valid) {
      this.cancelComment();
      return;
    }
    this.userService.createGuestInStorage({
      id: 'null',
      name: this.newGuestNameControl.value as string,
      lastName: '',
      email: ''
    })
    this.user = this.userService.getCurrentUserFromStorage();
  }

  getUserOrGuest(comment: Comment) {
    if (comment.user !== null) {
      return comment.user;
    } else {
      return {
        name: comment.guestName?.split(' ')[0],
        lastName: comment.guestName?.split(' ')[1] || '',
        color: DefaultValues.user.color
      } as User;
    }
  }
}
